import React, { useEffect, useCallback } from 'react'
import { getResizedImageUrl } from '../lib/cms'

export default function Lightbox({ images = [], currentIndex = 0, isOpen = false, onClose, onIndexChange }) {
  const total = images.length
  const currentImage = images[currentIndex]

  const handlePrev = useCallback(() => {
    onIndexChange((currentIndex - 1 + total) % total)
  }, [currentIndex, total, onIndexChange])

  const handleNext = useCallback(() => {
    onIndexChange((currentIndex + 1) % total)
  }, [currentIndex, total, onIndexChange])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') handlePrev()
      if (e.key === 'ArrowRight') handleNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, handlePrev, handleNext])

  if (!isOpen || !currentImage) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 backdrop-blur-md transition-opacity animate-in fade-in"
      onClick={onClose}
    >
      {/* Top action bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-4 sm:p-6 z-10 text-white">
        <div className="text-sm font-semibold tracking-wide bg-slate-900/60 backdrop-blur px-3 py-1.5 rounded-lg border border-slate-800">
          Billede {currentIndex + 1} af {total}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-white transition-colors border border-slate-700/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
          aria-label="Luk billedvisning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Main Image Container */}
      <div
        className="relative max-w-5xl max-h-[85vh] w-full p-4 flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getResizedImageUrl(currentImage, { width: 1600, quality: 85 })}
          alt={`Transport4U vognpark billede ${currentIndex + 1}`}
          className="max-h-[80vh] max-w-full object-contain rounded-xl shadow-2xl select-none"
        />

        {/* Navigation arrows (if more than 1 image) */}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-white transition-all border border-slate-700/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-white transition-all border border-slate-700/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {/* Bottom thumbnails hint */}
      {total > 1 && (
        <div
          className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-2 px-4 overflow-x-auto py-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onIndexChange(idx)}
              className={`h-12 w-16 shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                idx === currentIndex
                  ? 'border-sky-400 scale-105 ring-2 ring-sky-400/40'
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={getResizedImageUrl(img, { width: 120, height: 90, quality: 70 })}
                alt=""
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
